import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  correctTimeEvent,
  createManualAdjustment,
  createWorkerEvent,
} from "../api/timeevents";
import { buildCorrelationId } from "../lib/session";
import type {
  EventType,
  ManualAdjustmentInput,
  TimeEventCorrectionInput,
  WorkerEventInput,
} from "../api/types";

export interface DirectCorrectParams {
  eventId: string;
  occurredAt: string;
  reason?: string;
  // "attendance_out" pour fermer une session ouverte (résolution anomalie).
  // Omis = édition d'un fait (manual_adjustment).
  eventType?: "attendance_out";
}

function invalidateAllTimesheetReads(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: ["tawa", "anomalies"] });
  void qc.invalidateQueries({ queryKey: ["tawa", "worker", "timeline"] });
  void qc.invalidateQueries({ queryKey: ["tawa", "worker", "timesheets"] });
  void qc.invalidateQueries({ queryKey: ["tawa", "manager", "timesheets"] });
  // Vue weekly de la page Équipe (timeline + statut feuille) : rafraîchir après
  // une édition manager pour refléter events/totaux/statut à jour.
  void qc.invalidateQueries({ queryKey: ["tawa", "manager", "weekly"] });
  // L'état "session ouverte / badge out" dérive des sessions : une correction qui
  // ferme une session doit rafraîchir la vue open-sessions (partagée tenant-wide).
  void qc.invalidateQueries({ queryKey: ["tawa", "sessions", "open"] });
}

/** Mutation pour correction directe d'un time_event (admin/manager). */
export function useDirectCorrectTimeEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventId,
      occurredAt,
      reason,
      eventType,
    }: DirectCorrectParams) => {
      const body: TimeEventCorrectionInput = {
        occurred_at: occurredAt,
        correlation_id: buildCorrelationId(),
        ...(reason && reason.trim() ? { reason: reason.trim() } : {}),
        ...(eventType ? { event_type: eventType } : {}),
      };
      return correctTimeEvent(eventId, body);
    },
    onSuccess: () => invalidateAllTimesheetReads(qc),
  });
}

export interface CreateManualAdjustmentParams {
  workerUrl: string;
  eventType: EventType;
  occurredAt: string;
  reason: string;
}

/**
 * Mutation pour créer un time_event manuel (admin/manager) afin de combler
 * un trou — worker a oublié de badger. La raison est obligatoire.
 */
export function useCreateManualAdjustment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      workerUrl,
      eventType,
      occurredAt,
      reason,
    }: CreateManualAdjustmentParams) => {
      const body: ManualAdjustmentInput = {
        worker_url: workerUrl,
        event_type: eventType,
        occurred_at: occurredAt,
        reason: reason.trim(),
        correlation_id: buildCorrelationId(),
      };
      return createManualAdjustment(body);
    },
    onSuccess: () => invalidateAllTimesheetReads(qc),
  });
}

export interface CreateWorkerEventParams {
  eventType: EventType;
  occurredAt: string;
  reason: string;
  contextRefs?: Record<string, unknown>;
}

/**
 * Mutation worker self-service : crée un event sur sa propre timesheet draft
 * (insertion entre events, ajout libre). worker_url forcé côté serveur.
 */
export function useCreateWorkerEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      eventType,
      occurredAt,
      reason,
      contextRefs,
    }: CreateWorkerEventParams) => {
      const body: WorkerEventInput = {
        event_type: eventType,
        occurred_at: occurredAt,
        reason: reason.trim(),
        correlation_id: buildCorrelationId(),
        ...(contextRefs ? { context_refs: contextRefs } : {}),
      };
      return createWorkerEvent(body);
    },
    onSuccess: () => invalidateAllTimesheetReads(qc),
  });
}
